import axios, { AxiosResponse } from 'axios'

import { IAnnoPage, IAnnotation } from '../types/IAnnoPage'

interface IDrupalAnnotation {
  annotation: {
    'annotation-type': string
    'scroll-id': number
    'annotation-id': number
    coordinates: string
    'english-translation': string
    'modern-japanese-translation': string
    'traditional-japanese-transcription': string
    'image-annotation': string
  }
}

interface IDrupalData {
  annotations: IDrupalAnnotation[]
}

const getDrupalAnnotations = (manifest: string): Promise<IAnnoPage> => {
  if (typeof process.env.REACT_APP_API_ENDPOINT != 'string') {
    throw new TypeError(
      `REACT_APP_API_ENDPOINT needed to be a string but instead was: ${process.env.REACT_APP_API_ENDPOINT}`,
    )
  } else {
    return axios
      .get(process.env.REACT_APP_API_ENDPOINT, {
        transformResponse: (data: string) =>
          transformApiData(JSON.parse(data), manifest),
      })
      .then((response: AxiosResponse) => response.data)
      .catch((error) => error)
  }
}

const transformApiData = (
  apiData: IDrupalData,
  manifest: string,
): IAnnoPage => {
  const filteredData = apiData.annotations.filter(
    (anno) => anno.annotation['scroll-id'].toString() === manifest,
  )

  const annotations = filteredData.map(toAnnotations).flat()

  return {
    id: 'site_url/comments/page/1',
    type: 'AnnotationPage',
    items: annotations,
  }
}

const toAnnotations = (anno: IDrupalAnnotation): IAnnotation[] => {
  if (anno.annotation.coordinates === null) {
    return []
  }

  if (anno.annotation['annotation-type'] === 'Text') {
    return [
      {
        id: `site_url/annotations/${anno.annotation['annotation-id']}`,
        type: 'Annotation',
        motivation: 'commenting',
        body: [
          {
            type: 'TextualBody',
            value: anno.annotation['english-translation'],
            format: 'text/html',
          },
          {
            type: 'TextualBody',
            value: 'English',
            purpose: 'tagging',
            format: 'text/html',
          },
        ],
        target: `site_url/manifests/1/canvases/1#xywh=${anno.annotation.coordinates}`,
      },
      {
        id: `site_url/annotations/${anno.annotation['annotation-id']}`,
        type: 'Annotation',
        motivation: 'commenting',
        body: [
          {
            type: 'TextualBody',
            value: anno.annotation['modern-japanese-translation'],
            format: 'text/html',
          },
          {
            type: 'TextualBody',
            value: 'Japanese',
            purpose: 'tagging',
            format: 'text/html',
          },
        ],
        target: `site_url/manifests/1/canvases/1#xywh=${anno.annotation.coordinates}`,
      },
      {
        id: `site_url/annotations/${anno.annotation['annotation-id']}`,
        type: 'Annotation',
        motivation: 'commenting',
        body: [
          {
            type: 'TextualBody',
            value: `<div style='writing-mode: vertical-rl; text-orientation: upright; margin: auto;'>${anno.annotation['traditional-japanese-transcription']}</div>`,
            format: 'text/html',
          },
          {
            type: 'TextualBody',
            value: 'Transcription',
            purpose: 'tagging',
            format: 'text/html',
          },
        ],
        target: `site_url/manifests/1/canvases/1#xywh=${anno.annotation.coordinates}`,
      },
    ]
  }
  if (anno.annotation['annotation-type'] === 'Image') {
    return [
      {
        id: `site_url/annotations/${anno.annotation['annotation-id']}`,
        type: 'Annotation',
        motivation: 'commenting',
        body: [
          {
            type: 'TextualBody',
            value: anno.annotation['image-annotation'],
            format: 'text/html',
          },
          {
            type: 'TextualBody',
            value: 'Image',
            purpose: 'tagging',
            format: 'text/html',
          },
        ],
        target: `site_url/manifests/1/canvases/1#xywh=${anno.annotation.coordinates}`,
      },
    ]
  }
  return []
}

export default getDrupalAnnotations
